import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, Building2, Bell, FileText, Gauge, Sparkles } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const COMPANY = [
  ["name", "Ragione sociale"], ["vat", "Partita IVA"], ["address", "Indirizzo"],
  ["phone", "Telefono"], ["email", "Email"], ["iban", "IBAN (stampato sulla ricevuta)"],
];
const NOTIF = [
  ["job_scheduled", "Nuovo lavoro programmato", "La squadra assegnata riceve l'avviso appena crei il lavoro"],
  ["job_updated", "Modifiche al lavoro", "Orari, indirizzi, oggetti, squadra, stato"],
  ["extra_charges", "Aggiunte di costo", "I dipendenti ricevono l'avviso con la descrizione, mai l'importo"],
  ["attachments", "Foto, firme e ispezioni", "Ogni foto, firma o ispezione furgone caricata"],
  ["anomalies", "Anomalie carburante e km", "Avvisi all'admin"],
  ["incidents", "Allarme danni e incidenti", "Avvisa l'admin se note, aggiunte, ispezioni o presenze parlano di danni, infortuni o lamentele"],
  ["admin_email", "Email all'amministratore", "Oltre alle notifiche push"],
];
const THRESH = [
  ["fuel_pct", "Carburante: tolleranza (%)"], ["fuel_min_liters", "Carburante: eccesso minimo (litri)"],
  ["km_pct", "Km: scostamento dalla media (%)"], ["km_min_samples", "Km: lavori minimi per la media"],
];

function Section({ icon: Icon, title, children }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-4">
      <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
        <Icon className="w-5 h-5 text-sky-600" /> {title}
      </h2>
      {children}
    </section>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setS(r.data);
      setRecipients((r.data.daily_report.recipients || []).join(", "));
    });
  }, []);

  const set = (sec, k, v) => setS((p) => ({ ...p, [sec]: { ...p[sec], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...s, daily_report: { ...s.daily_report, recipients: recipients.split(/[,;\s]+/).filter(Boolean) } };
      const { data } = await api.put("/settings", body);
      setS(data);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="text-slate-400 text-sm">Caricamento…</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Solo amministratore</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Impostazioni</h1>
        </div>
        <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white h-11" data-testid="settings-save-button">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Salvataggio…" : "Salva impostazioni"}
        </Button>
      </header>

      <Section icon={Building2} title="Dati azienda sulle ricevute">
        <div className="grid sm:grid-cols-2 gap-4">
          {COMPANY.map(([k, label]) => (
            <div key={k}>
              <Label>{label}</Label>
              <Input value={s.company[k] || ""} onChange={(e) => set("company", k, e.target.value)} data-testid={`settings-company-${k}`} />
            </div>
          ))}
        </div>
      </Section>

      <Section icon={Bell} title="Quali notifiche inviare">
        <ul className="divide-y divide-slate-100">
          {NOTIF.map(([k, label, hint]) => (
            <li key={k} className="py-3 flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold text-slate-900 text-sm">{label}</div>
                <div className="text-xs text-slate-500">{hint}</div>
              </div>
              <Switch checked={!!s.notifications[k]} onCheckedChange={(v) => set("notifications", k, v)} data-testid={`settings-notif-${k}`} />
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={FileText} title="Report giornaliero">
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-slate-700">Genera e invia il report ogni giorno</div>
          <Switch checked={!!s.daily_report.enabled} onCheckedChange={(v) => set("daily_report", "enabled", v)} data-testid="settings-report-enabled" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Ora di invio (ora italiana)</Label>
            <select
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              value={s.daily_report.hour}
              onChange={(e) => set("daily_report", "hour", Number(e.target.value))}
              data-testid="settings-report-hour"
            >
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
            </select>
          </div>
          <div>
            <Label>Email destinatari (separate da virgola)</Label>
            <Input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="Vuoto = email degli admin" data-testid="settings-report-recipients" />
          </div>
        </div>
      </Section>

      <Section icon={Sparkles} title="Assistenza con Claude">
        <Label>Link ai tuoi artefatti Claude (uno per riga)</Label>
        <Textarea rows={3} defaultValue={(s.assistant?.artifact_links || []).join("\n")}
          onBlur={(e) => set("assistant", "artifact_links", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
          data-testid="settings-artifact-links" />
      </Section>

      <Section icon={Gauge} title="Soglie carburante e km">
        <div className="grid sm:grid-cols-2 gap-4">
          {THRESH.map(([k, label]) => (
            <div key={k}>
              <Label>{label}</Label>
              <Input type="number" min="0" step="any" value={s.thresholds[k]} onChange={(e) => set("thresholds", k, e.target.value)} data-testid={`settings-threshold-${k}`} />
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
